import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";
import {
  SettingsOutlinedIcon,
  LogoutIcon,
  LanguageOutlinedIcon,
  Person2OutlinedIcon,
  NotificationsNoneIcon,
} from "../access/icons/Icons";

import {
  Badge,
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import Navbar from "../components/Navbar";
import formatDate from "../components/FormatDate";
import "./adminLayout.scss";

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const doAutoLogout = () => {
    // brišemo SVE što utiče na auth/session state
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("isVerified");
    localStorage.removeItem("lastActive");
    // opciono: ako koristiš ovo za notifikacije
    // localStorage.removeItem("openedNotifs");

    navigate("/login");
  };

  // ✅ 1) Na mount: ako nema tokena, obriši lastActive da ne pravi loop
  useEffect(() => {
    const t = localStorage.getItem("token");

    // Ako nema tokena (npr. backend ga je invalidirao / istekao), obriši lastActive
    // da ne pravi loop i pošalji na login.
    if (!t) {
      localStorage.removeItem("lastActive");
      // opciono očisti i druge auth flagove koji umeju da ostanu
      localStorage.removeItem("role");
      localStorage.removeItem("isVerified");
      // ne diramo ništa drugo
      if (window.location.pathname !== "/login") {
        navigate("/login");
      }
      return;
    }

    // ako token postoji, a lastActive ne postoji, inicijalizuj ga
    const last = Number(localStorage.getItem("lastActive"));
    if (!last) {
      localStorage.setItem("lastActive", String(Date.now()));
      return;
    }

    // odmah proveri timeout (30 min)
    const now = Date.now();
    if (now - last > 1800000) {
      doAutoLogout();
    }
  }, [navigate]);

  // ✅ 2) Proveravaj inactivity periodično (da se stvarno izloguje i bez refresh-a)
  useEffect(() => {
    const interval = setInterval(() => {
      const t = localStorage.getItem("token");
      if (!t) {
        // Ako je token nestao (istek / ručni clear / 401 flow), obriši lastActive da ne ostaje zaglavljen.
        if (localStorage.getItem("lastActive")) {
          localStorage.removeItem("lastActive");
        }
        return;
      }

      const last = Number(localStorage.getItem("lastActive")) || Date.now();
      const now = Date.now();

      if (now - last > 1800000) {
        doAutoLogout();
      }
    }, 60 * 1000); // proveri na 60s

    return () => clearInterval(interval);
  }, [navigate]);

  // ✅ 3) Event listeneri: upisuj lastActive samo ako token postoji
  useEffect(() => {
    const updateActivity = () => {
      const t = localStorage.getItem("token");
      if (!t) return;
      localStorage.setItem("lastActive", String(Date.now()));
    };

    window.addEventListener("click", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("scroll", updateActivity);

    return () => {
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("scroll", updateActivity);
    };
  }, []);

  const role = localStorage.getItem("role");

  const [openSidebar, setOpenSidebar] = useState(window.innerWidth >= 960);
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const handleResize = () => setOpenSidebar(window.innerWidth >= 960);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 960) setOpenSidebar(false);
  }, [location.pathname]);

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  };

  const payload = parseJwt(token);
  const name = payload?.fullName || "";

  const allowedCategories = [
    "user_update",
    "hours",
    "pause",
    "project_finish",
    "comment",
    "reply",
    "settings",
  ];

  const fetchNotifications = () => {
    fetch(`${API_BASE_URL}/api/admin/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = data.map((n) => ({
          ...n,
          is_read:
            n.is_read === true ||
            n.is_read === 1 ||
            n.is_read === "1" ||
            n.is_read === "true",
        }));

        setNotifications(parsed);

        const unread = parsed.filter((n) => !n.is_read).length;

        setUnreadCount(unread);
      })
      .catch((err) => console.error("Failed to load notifications", err));
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // const handleLangClick = (e) => setLangAnchor(e.currentTarget);
  const handleProfileClick = (e) => setProfileAnchor(e.currentTarget);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setLangAnchor(null);
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("isVerified");
    localStorage.removeItem("lastActive");
    navigate("/login");
  };

  const handleOpenModal = async (notif) => {
    setAnchorEl(null);
    setModalOpen(true);
    setSelectedNotif(notif);

    // 💾 zapamti kao otvorenu
    const openedIds = JSON.parse(localStorage.getItem("openedNotifs") || "[]");
    if (!openedIds.includes(notif.id)) {
      const updated = [...openedIds, notif.id];
      localStorage.setItem("openedNotifs", JSON.stringify(updated));
      setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
    }

    // ⚙️ Ako backend podržava PATCH (opciono)
    try {
      await fetch(`${API_BASE_URL}/api/admin/notifications/${notif.id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
    } catch (err) {
      console.warn("PATCH /read failed (ignorisano)");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedNotif(null);
  };

  return (
    <div className="admin-layout">
      <Box
        sx={{
          position: window.innerWidth < 960 ? "absolute" : "relative",
          zIndex: 1200,
        }}
      >
        <Navbar
          openSidebar={openSidebar}
          setOpenSidebar={setOpenSidebar}
          isMobile={window.innerWidth <= 960}
        />
      </Box>

      <div className="admin-content">
        <div className="admin-header">
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <IconButton
              style={{ background: "#fff", color: "red" }}
              className="menu-toggle"
              onClick={() => setOpenSidebar(!openSidebar)}
              sx={{
                display: window.innerWidth < 960 ? "block" : "none",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 21,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      height: 2,
                      width: "100%",
                      bgcolor: "black",
                      borderRadius: 2,
                    }}
                  />
                ))}
              </Box>
            </IconButton>

            {location.pathname === "/admin/dashboard" && (
              <Typography className="welcome" variant="h6" fontWeight="light">
                Welcome,{" "}
                <Typography component="span" variant="h6" fontWeight="bold">
                  {name}
                </Typography>{" "}
                👋
              </Typography>
            )}
          </Box>

          <div className="icon-group">
            {/* 🌐 Language
            <IconButton
              onClick={handleLangClick}
              style={{ background: "#fff" }}
            >
              <LanguageOutlinedIcon className="header-icon" />
            </IconButton>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={handleMenuClose}
              PaperProps={{ style: { borderRadius: "20px" } }}
            >
              <MenuItem>English</MenuItem>
              <MenuItem>Srpski</MenuItem>
            </Menu> */}

            {/* 🔔 Notifications */}
            <IconButton onClick={handleMenuOpen} style={{ background: "#fff" }}>
              <Badge
                badgeContent={unreadCount > 0 ? unreadCount : null}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    minWidth: "18px",
                    height: "18px",
                    fontSize: "12px",
                    fontWeight: 600,
                    borderRadius: "50%",
                  },
                }}
              >
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  width: "300px",
                  borderRadius: "20px",
                  position: "relative",
                },
              }}
            >
              <Typography
                sx={{ px: 2, pt: 1, fontWeight: "bold", fontSize: 16 }}
              >
                Notifications
              </Typography>

              <Box sx={{ maxHeight: "250px", overflowY: "auto" }}>
                {notifications.length === 0 ? (
                  <MenuItem disabled>No notifications</MenuItem>
                ) : (
                  notifications.map((n) => {
                    const openedIds = JSON.parse(
                      localStorage.getItem("openedNotifs") || "[]"
                    );
                    const isOpened = openedIds.includes(n.id);

                    return (
                      <MenuItem
                        key={n.id}
                        onClick={() => handleOpenModal(n)}
                        sx={{
                          bgcolor: !isOpened ? "#f0f4ff" : "white",
                          borderBottom: "1px solid #eee",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                        }}
                      >
                        <Box>
                          <Typography
                            fontWeight={!isOpened ? "bold" : "normal"}
                            fontSize={12}
                          >
                            {n.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(n.created_at)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </Box>

              <MenuItem
                onClick={() => {
                  navigate(`/admin/notifications`);
                  setAnchorEl(null);
                }}
                sx={{
                  justifyContent: "center",
                  fontWeight: 500,
                  color: "#0b41cd",
                  borderTop: "1px solid #eee",
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  background: "#fff",
                  "&:hover": { background: "#fff" },
                }}
              >
                See all notifications →
              </MenuItem>
            </Menu>

            {/* 👤 Profile */}
            <IconButton
              onClick={handleProfileClick}
              style={{ background: "#fff" }}
            >
              <Person2OutlinedIcon className="header-icon" />
            </IconButton>
            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={handleMenuClose}
              PaperProps={{ style: { borderRadius: "20px" } }}
            >
              <MenuItem disabled>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography fontWeight="bold" fontSize={14}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => navigate("/admin/settings")}>
                <SettingsOutlinedIcon style={{ marginRight: 10 }} /> Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} style={{ color: "red" }}>
                <LogoutIcon style={{ marginRight: 10 }} /> Logout
              </MenuItem>
            </Menu>
          </div>
        </div>

        {/* Render children */}
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(child, {
                refreshNotifications: fetchNotifications,
              })
            : child
        )}

        {/* Modal */}
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Notification</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>{selectedNotif?.message}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedNotif?.created_at
                ? formatDate(selectedNotif.created_at)
                : ""}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminLayout;
